import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import { CampaignCard } from './CampaignCard';
import CampaignGridCard from './CampaignGridCard';
import { useAuthStore } from '../../store/authStore';
import { graphqlApi } from '../../services/graphql';
import { Campaign } from '../../types';
import { Spinner } from '../common/Spinner';
import { INTEREST_CATEGORIES } from '../../constants/categories';
import { useCampaignApplicationStats } from '../../hooks/useCampaignApplicationStats';

interface CampaignFilters {
  status?: string;
  category?: string;
  searchTerm?: string;
}

const CAMPAIGN_STATUSES = [
  'all',
  'draft',
  'active',
  'paused',
  'completed',
  'cancelled'
];

const CAMPAIGN_CATEGORIES = ['all', ...INTEREST_CATEGORIES];

export const CampaignsList: React.FC = () => {
  const { t } = useTranslation(['campaigns', 'common']);
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<CampaignFilters>({
    status: 'all',
    category: 'all',
    searchTerm: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const applicationStats = useCampaignApplicationStats(user?.hostId);

  useEffect(() => {
    if (user?.hostId) {
      fetchCampaigns();
    }
  }, [filters, user?.hostId]);

  const fetchCampaigns = async () => {
    if (!user?.hostId) {
      console.log('⚠️ No hostId available in user profile');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Build filter object for GraphQL query
      const queryFilters: any = {};

      if (filters.status && filters.status !== 'all') {
        queryFilters.status = filters.status;
      }

      if (filters.category && filters.category !== 'all') {
        queryFilters.category = filters.category;
      }

      console.log('🔍 Loading campaigns for host:', user.hostId);
      const response = await graphqlApi.getCampaignsByHost(user.hostId, queryFilters);

      if (response.success) {
        // GraphQL returns the data directly in response.data
        const baseCampaignData = response.data?.campaigns || [];

        // Apply client-side search filter if needed
        const campaignData = filters.searchTerm ?
          baseCampaignData.filter((campaign: Campaign) => {
            const searchLower = filters.searchTerm!.toLowerCase();
            return (
            (campaign.campaignName || campaign.title || '').toLowerCase().includes(searchLower) ||
            (campaign.productIntroduction || campaign.description || '').toLowerCase().includes(searchLower) ||
            (campaign.campaignCategory || campaign.category || '').toLowerCase().includes(searchLower)
            );
          }) : baseCampaignData;

        setCampaigns(campaignData);
        console.log('✅ Campaigns loaded:', campaignData.length);
      } else {
        toast.error(response.error || t('messages.loadingError'));
      }
    } catch (error: any) {
      console.error('Error fetching campaigns:', error);
      toast.error(error.message || t('messages.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof CampaignFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      category: 'all',
      searchTerm: '',
    });
  };

  const getFilterSummary = () => {
    const activeFilters: string[] = [];

    if (filters.status && filters.status !== 'all') {
      activeFilters.push(`${t('list.statusLabel')}: ${filters.status}`);
    }

    if (filters.category && filters.category !== 'all') {
      activeFilters.push(`${t('list.categoryLabel')}: ${filters.category}`);
    }

    if (filters.searchTerm) {
      activeFilters.push(`${t('list.searchPlaceholder')}: ${filters.searchTerm}`);
    }

    return activeFilters.length > 0
      ? t('list.filteredBy', { filters: activeFilters.join(', ') })
      : t('list.allCampaigns');
  };

  const handleViewDetails = (campaign: Campaign) => {
    navigate(`/campaigns/${campaign.campaignId}/detail`);
  };

  const renderHeader = () => (
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('list.title')}</h1>
        <p className="text-gray-600 mt-2">
          {t('list.subtitle')}
        </p>
      </div>

      <div className="flex items-center space-x-4">
        <Link
          to="/campaigns/register"
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          {t('list.createCampaign')}
        </Link>
      </div>
    </div>
  );

  const renderSearchAndFilters = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search Bar */}
        <div className="flex-1">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('list.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
            />
          </div>
        </div>

        {/* Filter Button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <FunnelIcon className="h-5 w-5 mr-2" />
          {t('list.filters')}
          {(filters.status !== 'all' || filters.category !== 'all') && (
            <span className="ml-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
              {[filters.status !== 'all' ? 1 : 0, filters.category !== 'all' ? 1 : 0].reduce((a, b) => a + b, 0)}
            </span>
          )}
        </button>
      </div>

      {/* Expandable Filters */}
      {showFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('list.statusLabel')}
              </label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                {CAMPAIGN_STATUSES.map(status => (
                  <option key={status} value={status}>
                    {status === 'all' ? t('list.allStatuses') : status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('list.categoryLabel')}
              </label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                {CAMPAIGN_CATEGORIES.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? t('list.allCategories') : t(`common:categories.${category}`, category)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
              >
                {t('list.clearFilters')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderCampaignStats = () => {
    const stats = {
      total: campaigns.length,
      active: campaigns.filter(c => c.status === 'active').length,
      draft: campaigns.filter(c => c.status === 'draft').length,
      completed: campaigns.filter(c => c.status === 'completed').length,
    };

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600">{t('list.totalCampaigns')}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          <div className="text-sm text-gray-600">{t('list.active')}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-yellow-600">{stats.draft}</div>
          <div className="text-sm text-gray-600">{t('list.draft')}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-blue-600">{stats.completed}</div>
          <div className="text-sm text-gray-600">{t('list.completed')}</div>
        </div>
      </div>
    );
  };

  const renderCampaignGrid = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <Spinner />
        </div>
      );
    }

    if (!user?.hostId) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('list.noHostProfile')}
          </h3>
          <p className="text-gray-600">
            {t('list.createHostProfile')}
          </p>
        </div>
      );
    }

    if (campaigns.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {filters.searchTerm || filters.status !== 'all' || filters.category !== 'all'
              ? t('list.noMatchingCampaigns')
              : t('list.noCampaigns')
            }
          </h3>
          <p className="text-gray-600 mb-6">
            {filters.searchTerm || filters.status !== 'all' || filters.category !== 'all'
              ? t('list.adjustFilters')
              : t('list.createFirstCampaign')
            }
          </p>
          {!(filters.searchTerm || filters.status !== 'all' || filters.category !== 'all') && (
            <Link
              to="/campaigns/register"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {t('list.createYourFirstCampaign')}
            </Link>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">
            {getFilterSummary()} ({t('list.campaignCount', { count: campaigns.length })})
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((campaign, idx) => (
            <CampaignGridCard
              key={campaign.campaignId}
              campaign={campaign}
              index={idx}
              userType={user?.userType}
              applicationStats={applicationStats[campaign.campaignId]}
              onStatusChange={(cid, newStatus) =>
                setCampaigns((prev) =>
                  prev.map((x) =>
                    x.campaignId === cid ? ({ ...x, status: newStatus } as Campaign) : x,
                  ),
                )
              }
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {renderHeader()}
      {renderCampaignStats()}
      {renderSearchAndFilters()}
      {renderCampaignGrid()}
    </div>
  );
};
